import ReactMarkdown from "react-markdown"
import type { Components } from "react-markdown"

import { coordinator } from "@/lib/player/coordinator"
import type { PlayableItem } from "@/lib/player/playable-item"
import { cn } from "@/lib/utils"

import { FOCUS_RING, HairlineRule } from "./jacket-chrome"
import { estimateReadingMinutes } from "./reading-time"
import { SERIF, bylineName, chromeLabel, readerBody } from "./type-voices"

/**
 * The shell-native article reader — the §7.4 deep dive. ContentPlayer's
 * LiveArticle is deliberately not mounted for articles (its prose chrome
 * breaks the jacket language and it never emits ended, which would stall
 * Up Next); this reader renders `item.body` (Markdown) in the §3.3 reader
 * row and gives the read its one end: the colophon's "turn the page"
 * affordance → coordinator.emitEnded — the sanctioned end-of-media pathway.
 * No scroll-sentinel auto-end: the reader decides when the read is done.
 *
 * The scroll region owns its own gesture (`data-reader-scroll` pairs with
 * the shells' watchDrag predicate) and its own keys
 * (`data-player-keys-ignore` — arrows scroll text, Escape still bubbles).
 */
export function ArticleReader({
  item,
  turnAccent = "ink",
  className,
}: {
  item: PlayableItem
  /** C spends its one yellow on the turn action; A and B keep it ink. */
  turnAccent?: "ink" | "yellow"
  className?: string
}) {
  const minutes = item.body ? estimateReadingMinutes(item.body) : null
  return (
    <div
      tabIndex={0}
      role="region"
      aria-label={`Article — ${item.title}`}
      data-reader-scroll
      data-player-keys-ignore
      className={cn(
        "jacket-reader-scroll h-full overflow-y-auto focus:outline-none",
        className,
      )}
    >
      <div className="flex flex-col gap-4 px-5 py-6">
        <header className="flex max-w-[65ch] items-baseline justify-between gap-3">
          <span className="text-batyr-ink/60" style={chromeLabel}>
            print
          </span>
          {minutes !== null && (
            <span
              className="text-batyr-ink/60"
              style={{ ...chromeLabel, fontVariantNumeric: "oldstyle-nums" }}
            >
              {minutes} min read
            </span>
          )}
        </header>
        {/* The piece's masthead — relocated off the jacket face so the reader
            row carries its own title above the galley (the sidebar is now the
            note column). Full title, not splitTitle's head: the galley has the
            width the jacket face lacked. */}
        <h1
          className="max-w-[65ch]"
          style={{
            fontFamily: SERIF,
            fontWeight: 640,
            fontSize: "clamp(26px, 3.2vw, 34px)",
            lineHeight: 1.14,
            color: "var(--color-batyr-ink)",
          }}
        >
          {item.title}
        </h1>
        <HairlineRule className="max-w-[65ch]" />
        {item.body ? (
          <div className="jacket-reader" style={readerBody}>
            <ReactMarkdown components={READER_COMPONENTS}>
              {item.body}
            </ReactMarkdown>
          </div>
        ) : (
          <p style={readerBody}>
            This piece lives at its source —{" "}
            <a
              href={item.source_url}
              target="_blank"
              rel="noopener noreferrer"
              className="underline underline-offset-2"
              style={{ color: "var(--color-batyr-blue)" }}
            >
              read it there
            </a>
            .
          </p>
        )}
        <footer className="mt-2 flex max-w-[65ch] flex-col items-start gap-3 pb-4">
          <HairlineRule className="w-14" />
          <button
            type="button"
            // End-of-read: the reader's explicit end — the only article
            // pathway into Up Next.
            onClick={() => coordinator.emitEnded(item.id)}
            className={cn(
              "cursor-pointer rounded-xs px-3 py-1.5 shadow-xs",
              FOCUS_RING,
              turnAccent === "yellow"
                ? "bg-brand-solid"
                : "bg-surface-elevated text-batyr-ink",
            )}
            style={chromeLabel}
          >
            turn the page
          </button>
          {item.source_url && (
            <a
              href={item.source_url}
              target="_blank"
              rel="noopener noreferrer"
              className="underline-offset-2 hover:underline"
              style={bylineName}
            >
              read at the source ↗
            </a>
          )}
        </footer>
      </div>
    </div>
  )
}

/**
 * Reader-scoped markdown rendering. RichText's map is deliberately not
 * reused: its blockquote leans on border-brand-solid (a second yellow) and
 * its type breaks the reader row.
 */
const READER_COMPONENTS: Components = {
  p: ({ children }) => <p className="my-3">{children}</p>,
  a: ({ href, children }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="underline underline-offset-2"
      style={{ color: "var(--color-batyr-blue)" }}
    >
      {children}
    </a>
  ),
  blockquote: ({ children }) => (
    <blockquote className="my-4 border-l border-[rgb(var(--batyr-ink-rgb)/0.16)] pl-4 italic">
      {children}
    </blockquote>
  ),
  h1: ({ children }) => <ReaderHeading>{children}</ReaderHeading>,
  h2: ({ children }) => <ReaderHeading>{children}</ReaderHeading>,
  h3: ({ children }) => <ReaderHeading>{children}</ReaderHeading>,
  strong: ({ children }) => (
    <strong style={{ fontWeight: 620 }}>{children}</strong>
  ),
  em: ({ children }) => <em className="italic">{children}</em>,
}

function ReaderHeading({ children }: { children?: React.ReactNode }) {
  return (
    <h2
      className="mt-6 mb-2"
      style={{
        fontFamily: SERIF,
        fontWeight: 560,
        fontSize: 22,
        lineHeight: 1.3,
        color: "var(--color-batyr-ink)",
      }}
    >
      {children}
    </h2>
  )
}
